import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import logger from '@/lib/logger';

export type VoteType = 'up' | 'down'
export type FeedbackItemType = 'suggested_action' | 'entity' | 'transcript' | 'activity_event' | 'chat' | 'quick_note'

export interface LLMAnalysis {
  reasoning: string           // LLM's interpretation of WHY user voted
  patterns: string[]          // Detected patterns/behaviors
  suggestions: string[]       // Improvement suggestions
}

export interface CallContextSnapshot {
  roomName: string
  conversationPhase: string
  entities: any               // Entities snapshot
  transcript: string[]        // Recent transcript (last 10 messages)
  suggestedActions: any[]     // Current suggested actions
  activityFeed: string[]      // Current activity events
}

export interface FeedbackItem {
  id: string                    // Unique feedback event ID
  itemType: FeedbackItemType    // Type of item being rated
  itemId: string                // ID of the item being rated
  vote: VoteType                // User's vote
  timestamp: string             // ISO timestamp

  // Captured context at time of feedback
  itemData: Record<string, any> // Full item data snapshot
  callContext: CallContextSnapshot

  // LLM-generated analysis (populated after backend response)
  llmAnalysis?: LLMAnalysis
  llmAnalysisStatus: 'pending' | 'loading' | 'complete' | 'error'

  // User can also rate the AI's interpretation
  interpretationVote?: VoteType
  interpretationVoteTimestamp?: string
}

const DEBUG_FEEDBACK = process.env.NEXT_PUBLIC_FEEDBACK_DEBUG === 'true'

export interface FeedbackState {
  feedbackItems: FeedbackItem[]
  pendingFeedback: string[]     // IDs of feedback awaiting LLM analysis
}

const initialState: FeedbackState = {
  feedbackItems: [],
  pendingFeedback: []
}

const feedbackSlice = createSlice({
  name: 'feedback',
  initialState,
  reducers: {
    hydrateFeedbackState: (_state, action: PayloadAction<Partial<FeedbackState>>) => {
      return {
        ...initialState,
        ...action.payload,
      }
    },
    /**
     * Submit new feedback on an item.
     * Creates a new feedback entry with pending LLM analysis status.
     */
    submitFeedback: (state, action: PayloadAction<{
      id: string
      itemType: FeedbackItemType
      itemId: string
      itemData: Record<string, any>
      vote: VoteType
      callContext: CallContextSnapshot
    }>) => {
      const { id, itemType, itemId, itemData, vote, callContext } = action.payload

      if (DEBUG_FEEDBACK) {
        logger.debug('feedbackSlice', 'submitFeedback reducer called', {
          id,
          itemType,
          itemId,
          vote,
          currentFeedbackItemsCount: state.feedbackItems.length
        })
      }

      // Check if feedback already exists for this item
      const existingIndex = state.feedbackItems.findIndex(f => f.itemId === itemId)
      if (DEBUG_FEEDBACK) {
        logger.debug('feedbackSlice', 'existingIndex', { existingIndex })
      }

      if (existingIndex !== -1) {
        // Update existing feedback (user changed their vote)
        const existing = state.feedbackItems[existingIndex]
        if (DEBUG_FEEDBACK) {
          logger.debug('feedbackSlice', 'Found existing feedback', { existingVote: existing.vote, newVote: vote })
        }
        if (existing.vote === vote) {
          // Same vote - remove the feedback (toggle off)
          if (DEBUG_FEEDBACK) {
            logger.debug('feedbackSlice', 'Same vote - removing feedback')
          }
          state.feedbackItems.splice(existingIndex, 1)
          state.pendingFeedback = state.pendingFeedback.filter(pid => pid !== existing.id)
          return
        }
        // Different vote - update it
        if (DEBUG_FEEDBACK) {
          logger.debug('feedbackSlice', 'Different vote - updating existing feedback')
        }
        state.feedbackItems[existingIndex] = {
          ...existing,
          id,
          vote,
          timestamp: new Date().toISOString(),
          itemData,
          callContext,
          llmAnalysis: undefined,  // Reset analysis for new vote
          llmAnalysisStatus: 'pending',
          interpretationVote: undefined,
          interpretationVoteTimestamp: undefined
        }
        state.pendingFeedback.push(id)
      } else {
        // New feedback
        if (DEBUG_FEEDBACK) {
          logger.debug('feedbackSlice', 'Creating new feedback item')
        }
        const newFeedback: FeedbackItem = {
          id,
          itemType,
          itemId,
          vote,
          timestamp: new Date().toISOString(),
          itemData,
          callContext,
          llmAnalysisStatus: 'pending'
        }
        state.feedbackItems.push(newFeedback)
        state.pendingFeedback.push(id)
        if (DEBUG_FEEDBACK) {
          logger.debug('feedbackSlice', 'New feedback added. Total items', { data: state.feedbackItems.length })
        }
      }
    },

    /**
     * Mark feedback as loading (sent to backend for LLM analysis).
     */
    setFeedbackLoading: (state, action: PayloadAction<string>) => {
      const feedback = state.feedbackItems.find(f => f.id === action.payload)
      if (feedback) {
        feedback.llmAnalysisStatus = 'loading'
      }
    },

    /**
     * Receive LLM analysis from backend.
     */
    receiveFeedbackAnalysis: (state, action: PayloadAction<{
      feedbackId: string
      llmAnalysis: LLMAnalysis
    }>) => {
      const { feedbackId, llmAnalysis } = action.payload
      const feedback = state.feedbackItems.find(f => f.id === feedbackId)
      if (feedback) {
        feedback.llmAnalysis = llmAnalysis
        feedback.llmAnalysisStatus = 'complete'
      }
      state.pendingFeedback = state.pendingFeedback.filter(id => id !== feedbackId)
    },

    /**
     * Handle LLM analysis error.
     */
    setFeedbackError: (state, action: PayloadAction<string>) => {
      const feedback = state.feedbackItems.find(f => f.id === action.payload)
      if (feedback) {
        feedback.llmAnalysisStatus = 'error'
      }
      state.pendingFeedback = state.pendingFeedback.filter(id => id !== action.payload)
    },

    /**
     * Rate the AI's interpretation (meta-feedback).
     * User can give thumbs up/down to how well the AI understood their vote.
     */
    rateInterpretation: (state, action: PayloadAction<{
      feedbackId: string
      vote: VoteType
    }>) => {
      const { feedbackId, vote } = action.payload
      const feedback = state.feedbackItems.find(f => f.id === feedbackId)
      if (feedback) {
        // Toggle off if same vote
        if (feedback.interpretationVote === vote) {
          feedback.interpretationVote = undefined
          feedback.interpretationVoteTimestamp = undefined
        } else {
          feedback.interpretationVote = vote
          feedback.interpretationVoteTimestamp = new Date().toISOString()
        }
      }
    },

    /**
     * Remove feedback for an item (user clicked same vote to toggle off).
     */
    removeFeedback: (state, action: PayloadAction<string>) => {
      const index = state.feedbackItems.findIndex(f => f.itemId === action.payload)
      if (index !== -1) {
        const feedbackId = state.feedbackItems[index].id
        state.feedbackItems.splice(index, 1)
        state.pendingFeedback = state.pendingFeedback.filter(id => id !== feedbackId)
      }
    },

    /**
     * Clear all feedback (on room leave/reset).
     */
    clearFeedback: (state) => {
      state.feedbackItems = []
      state.pendingFeedback = []
    },

    /**
     * Load feedback from persisted state (e.g., on reconnect).
     */
    loadFeedback: (state, action: PayloadAction<FeedbackItem[]>) => {
      state.feedbackItems = action.payload
      // Re-populate pending list for any items without analysis
      state.pendingFeedback = action.payload
        .filter(f => f.llmAnalysisStatus === 'pending' || f.llmAnalysisStatus === 'loading')
        .map(f => f.id)
    }
  }
})

export const {
  hydrateFeedbackState,
  submitFeedback,
  setFeedbackLoading,
  receiveFeedbackAnalysis,
  setFeedbackError,
  rateInterpretation,
  removeFeedback,
  clearFeedback,
  loadFeedback
} = feedbackSlice.actions

export default feedbackSlice.reducer
